import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import Input from '../ui/Input';
import RequiredDocumentsPanel from './RequiredDocumentsPanel';
import RejectDialog from './RejectDialog';
import FieldAnswerList from '../fields/FieldAnswerList';
import { EditRequestModal, PendingEditBanner } from './StepEditRequest';
import { processCandidateStep } from '../../api/candidates';
import { pushToast } from '../ui/toastBus';
import { usePermission } from '../../auth/usePermission';
import { formatValidationErrors } from '../../utils/errors';
import { groupFieldsBySection } from '../../utils/fieldSections';

const META_FIELD = {
  swipe_card: { key: 'employee_no', label: 'Employee No', required: true },
  permit_issuance: { key: 'permit_no', label: 'Permit No', required: false },
};

const FIELDS_GRID = 'grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2';

function actionsFor(step) {
  if (step.kind !== 'decision') return [null, null];
  const positive = step.decision_labels?.positive === 'Approved' ? 'approve' : 'pass';
  const negative = step.decision_labels?.negative === 'Rejected' ? 'reject' : 'fail';
  return [positive, negative];
}

export default function CurrentStepPanel({ candidate, step }) {
  const queryClient = useQueryClient();
  const { can } = usePermission();

  // allowed_actions from the candidate is only computed for the literal current step; a step
  // being caught up out of order needs the same permission + document check done locally.
  const permissionName = `step.process.${step.code}`;
  const hasPermission = can(permissionName);
  const allowed = step.is_current
    ? candidate.allowed_actions
    : {
        can_process: hasPermission && (step.missing_documents?.length ?? 0) === 0,
        missing_permission: hasPermission ? null : permissionName,
        missing_documents: step.missing_documents || [],
      };
  // Skip bypasses documents/fields entirely, so it's gated on permission alone — not on the
  // same missing-documents check that blocks the normal complete/approve/reject actions.
  // Already-skipped steps don't offer it again — they're here to be completed, not re-skipped.
  const canSkip = step.skip_enabled && hasPermission && step.status !== 'skipped';
  // The server already confirms this is the current in-progress task step with at least one
  // section saved — documents/checklist/other sections don't gate moving on early.
  const canMoveOn = !!step.can_move_on && hasPermission;

  const [remarks, setRemarks] = useState(step.remarks || '');
  const [checklistState, setChecklistState] = useState(step.checklist_state || {});
  const [answers, setAnswers] = useState(step.answers || {});
  const [metaValue, setMetaValue] = useState(step.meta?.[META_FIELD[step.code]?.key] || '');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const canRequestEdit = can('edit candidate completed step');

  const metaField = META_FIELD[step.code];

  const mutation = useMutation({
    mutationFn: (payload) => processCandidateStep(candidate.id, step.candidate_step_id, payload),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Step updated.' });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
      setRejectOpen(false);
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      const message = (errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not process step.';
      pushToast({ type: 'error', message });
    },
  });

  // Separate from `mutation` so a section Update / Next click doesn't show the main Complete
  // button as loading (and vice versa).
  const sectionMutation = useMutation({
    mutationFn: (payload) => processCandidateStep(candidate.id, step.candidate_step_id, payload),
    onSuccess: (_data, variables) => {
      pushToast({ type: 'success', message: variables.action === 'next' ? 'Moved to next step.' : 'Section saved.' });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      const message = (errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not save section.';
      pushToast({ type: 'error', message });
    },
  });

  const buildPayload = (action, overrideRemarks) => ({
    action,
    remarks: overrideRemarks ?? remarks,
    checklist_state: checklistState,
    answers,
    meta: metaField && metaValue ? { [metaField.key]: metaValue } : undefined,
  });

  const handlePositive = (action) => mutation.mutate(buildPayload(action));
  const handleReject = (action, reason) => mutation.mutate(buildPayload(action, reason));
  const handleSkip = () => mutation.mutate(buildPayload('skip'));

  const saveSection = (sectionId, sectionFields) => {
    const sectionAnswers = {};
    sectionFields.forEach((f) => {
      sectionAnswers[f.id] = answers[f.id];
    });
    sectionMutation.mutate({ action: 'save_section', section_id: sectionId, answers: sectionAnswers });
  };
  const moveOn = () => sectionMutation.mutate({ action: 'next' });

  const toggleChecklistItem = (item) => {
    setChecklistState((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  const setAnswer = (fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }));

  const [positiveAction, negativeAction] = actionsFor(step);
  const isReprocessableFail = step.status === 'failed' && step.negative_outcome === 'company_policy';

  // Field sections only drive incremental save + early "Next" for task steps — a decision step
  // is a single all-or-nothing action, so its fields (sectioned or not) render as one flat form.
  const sections = step.kind === 'task' ? step.field_sections || [] : [];
  const bySection = sections.length > 0 ? groupFieldsBySection(step.fields || []) : null;
  const ungroupedFields = bySection ? bySection.get(null) || [] : step.fields || [];

  return (
    <div className="rounded-lg border border-primary-light bg-primary-50/40 p-4">
      {step.description && <p className="mb-3 text-sm text-gray-600">{step.description}</p>}

      {isReprocessableFail && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Previous attempt failed{step.meta?.attempts?.length ? ` (${step.meta.attempts.length} attempt(s))` : ''} —
          you can retry.
        </p>
      )}

      {step.checklist?.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Checklist</h4>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
            {step.checklist.map((item) => (
              <label key={item} className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" className="accent-primary" checked={!!checklistState[item]} onChange={() => toggleChecklistItem(item)} />
                {item}
              </label>
            ))}
          </div>
        </div>
      )}

      <PendingEditBanner candidate={candidate} step={step} />

      {bySection &&
        sections.map((section) => {
          const sectionFields = bySection.get(section.id) || [];
          if (sectionFields.length === 0) return null;
          const saved = (step.completed_sections || []).includes(section.id);

          return (
            <div key={section.id} className="mb-4 rounded-md border border-gray-200 bg-white/60 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {section.title}
                  {saved && <span className="ml-2 normal-case text-green-600">✓ Saved</span>}
                </h4>
                {!saved && hasPermission && (
                  <Button
                    variant="secondary"
                    className="px-3 py-1.5 text-xs"
                    onClick={() => saveSection(section.id, sectionFields)}
                    loading={sectionMutation.isPending && sectionMutation.variables?.section_id === section.id}
                  >
                    Update section
                  </Button>
                )}
                {saved && !step.pending_edit_request && canRequestEdit && (
                  <Button variant="secondary" className="px-3 py-1.5 text-xs" onClick={() => setEditSection(section)}>
                    Request edit
                  </Button>
                )}
              </div>
              {saved ? (
                <div className="pointer-events-none opacity-60">
                  <FieldAnswerList fields={sectionFields} answers={step.answers} onAnswer={() => {}} className={FIELDS_GRID} />
                </div>
              ) : (
                <FieldAnswerList fields={sectionFields} answers={answers} onAnswer={setAnswer} className={FIELDS_GRID} />
              )}
            </div>
          );
        })}

      {editSection && (
        <EditRequestModal
          open={!!editSection}
          onClose={() => setEditSection(null)}
          candidate={candidate}
          step={step}
          sectionId={editSection.id}
          sectionTitle={editSection.title}
          fields={bySection?.get(editSection.id) || []}
        />
      )}

      {ungroupedFields.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{sections.length > 0 ? 'Other fields' : 'Fields'}</h4>
          <FieldAnswerList fields={ungroupedFields} answers={answers} onAnswer={setAnswer} className={FIELDS_GRID} />
        </div>
      )}

      {step.required_documents?.length > 0 && (
        <div className="mb-4">
          <RequiredDocumentsPanel
            requiredDocuments={step.required_documents}
            documents={candidate.documents}
            candidateId={candidate.id}
            candidateStepId={step.candidate_step_id}
            fields={step.fields}
            answers={answers}
          />
        </div>
      )}

      {metaField && (
        <Input
          label={metaField.label}
          required={metaField.required}
          value={metaValue}
          onChange={(e) => setMetaValue(e.target.value)}
          className="mb-4"
        />
      )}

      <Textarea label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mb-4" />

      {allowed.can_process || canSkip || canMoveOn ? (
        <div className="flex flex-wrap gap-3">
          {allowed.can_process &&
            (step.kind === 'task' ? (
              <Button onClick={() => handlePositive('complete')} loading={mutation.isPending}>
                Mark Complete
              </Button>
            ) : (
              <>
                <Button onClick={() => handlePositive(positiveAction)} loading={mutation.isPending} className="capitalize">
                  {positiveAction}
                </Button>
                <Button variant="danger" onClick={() => setRejectOpen(true)} disabled={mutation.isPending} className="capitalize">
                  {negativeAction}
                </Button>
              </>
            ))}
          {canSkip && (
            <Button variant="secondary" onClick={handleSkip} loading={mutation.isPending}>
              Skip Step
            </Button>
          )}
          {canMoveOn && (
            <Button variant="secondary" onClick={moveOn} loading={sectionMutation.isPending && sectionMutation.variables?.action === 'next'}>
              Next step →
            </Button>
          )}
        </div>
      ) : (
        <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">
          {allowed.missing_permission
            ? `Requires permission: ${allowed.missing_permission}`
            : allowed.missing_documents?.length > 0
              ? `Missing verified documents: ${allowed.missing_documents.join(', ')}`
              : 'You cannot process this step.'}
        </p>
      )}

      {step.kind === 'decision' && (
        <RejectDialog
          open={rejectOpen}
          onClose={() => setRejectOpen(false)}
          onConfirm={(reason) => handleReject(negativeAction, reason)}
          loading={mutation.isPending}
          actionLabel={negativeAction === 'reject' ? 'Reject' : 'Fail'}
          closesRecruitment={step.negative_outcome === 'reject_candidate'}
        />
      )}
    </div>
  );
}
